import { apiClient, type Paginated } from '@/core/api';
import type {
  CreatePupilInput,
  CreateParentLinkInput,
  MemberDto,
  MemberListParams,
  MemberUpdateInput,
  MessageableMemberDto,
  MessageableMemberParams,
  ParentLinkDto,
  ParentLinkListParams,
} from './types';

/** `GET /api/members/` — school member directory (admin/teacher only). */
export async function getMembers(params: MemberListParams = {}): Promise<Paginated<MemberDto>> {
  const { data } = await apiClient.get<Paginated<MemberDto>>('/members/', { params });
  return data;
}

/**
 * `GET /api/members/messageable/` — the members the CALLER may start a direct
 * thread with, for EVERY role.
 *
 * The server computes this from the same function that authorizes
 * `POST /threads/direct/` (backend ADR-020), so the rows are exactly the pairs
 * it would accept: the client never re-derives who may talk to whom.
 */
export async function getMessageableMembers(
  params: MessageableMemberParams = {}
): Promise<Paginated<MessageableMemberDto>> {
  const { data } = await apiClient.get<Paginated<MessageableMemberDto>>('/members/messageable/', {
    params,
  });
  return data;
}

/** `PATCH /api/members/{public_id}/` — admin-only deactivate/reactivate. */
export async function updateMember(publicId: string, input: MemberUpdateInput): Promise<MemberDto> {
  const { data } = await apiClient.patch<MemberDto>(`/members/${publicId}/`, input);
  return data;
}

/**
 * `GET /api/parent-links/` — the school's parent↔child links (admin/teacher).
 *
 * Filter by `pupil` to read a pupil's parents, or by `parent` to read a
 * parent's children — the two questions the same table answers (ADR-007).
 */
/** `POST /api/members/create-pupil/` — mint a pupil account with invented
 *  credentials (ADR-038). The password is sent once and never echoed back. */
export async function createPupil(input: CreatePupilInput): Promise<MemberDto> {
  const { data } = await apiClient.post<MemberDto>('/members/create-pupil/', input);
  return data;
}

/** `POST /api/members/{id}/set-password/` — mailbox-less recovery (ADR-038):
 *  pupils only, refused when the user belongs to another school too. */
export async function setMemberPassword(publicId: string, password: string): Promise<void> {
  await apiClient.post(`/members/${publicId}/set-password/`, { password });
}

export async function getParentLinks(
  params: ParentLinkListParams = {}
): Promise<Paginated<ParentLinkDto>> {
  const { data } = await apiClient.get<Paginated<ParentLinkDto>>('/parent-links/', { params });
  return data;
}

/** `POST /api/parent-links/` — link a parent member to a pupil member. */
export async function createParentLink(input: CreateParentLinkInput): Promise<ParentLinkDto> {
  const { data } = await apiClient.post<ParentLinkDto>('/parent-links/', input);
  return data;
}

/** `DELETE /api/parent-links/{public_id}/` — removes the access only; the
 *  pupil's history is untouched (ADR-007). */
export async function deleteParentLink(publicId: string): Promise<void> {
  await apiClient.delete(`/parent-links/${publicId}/`);
}
